/**
 * Pure hex color math shared by the web app (tenant theming) and server-side
 * email rendering. No 'server-only' or 'use client' — importable everywhere.
 */

const HEX_6 = /^#([0-9A-Fa-f]{2})([0-9A-Fa-f]{2})([0-9A-Fa-f]{2})$/;

export function isHexColor(value: unknown): value is string {
  return typeof value === 'string' && HEX_6.test(value);
}

export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const result = HEX_6.exec(hex);

  if (!result) return { r: 0, g: 0, b: 0 };

  return {
    r: parseInt(result[1]!, 16),
    g: parseInt(result[2]!, 16),
    b: parseInt(result[3]!, 16),
  };
}

export function rgbToHex(r: number, g: number, b: number): string {
  return (
    '#' +
    [r, g, b]
      .map((v) =>
        Math.round(Math.max(0, Math.min(255, v)))
          .toString(16)
          .padStart(2, '0'),
      )
      .join('')
  );
}

const SRGB_TO_LINEAR = Array.from({ length: 256 }, (_, value) => {
  const s = value / 255;

  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
});

/** WCAG 2.x relative luminance of 0–255 channels (0 = black, 1 = white). */
export function rgbLuminance(r: number, g: number, b: number): number {
  return (
    0.2126 * SRGB_TO_LINEAR[r]! +
    0.7152 * SRGB_TO_LINEAR[g]! +
    0.0722 * SRGB_TO_LINEAR[b]!
  );
}

/** WCAG 2.x relative luminance (0 = black, 1 = white). */
export function luminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex);

  return rgbLuminance(r, g, b);
}

export function isDarkColor(hex: string): boolean {
  return luminance(hex) < 0.5;
}

/**
 * WCAG 2.x relative-contrast ratio between two hex colors (1 = identical,
 * 21 = black on white). Used by the website builder to warn a tenant before
 * they publish an unreadable canvas/ink or accent color — the pickers are
 * free-form, so nothing else stops `#ffffff` text on a `#ffffff` canvas.
 */
export function contrastRatio(hexA: string, hexB: string): number {
  const a = luminance(hexA);
  const b = luminance(hexB);
  const lighter = Math.max(a, b);
  const darker = Math.min(a, b);

  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Pick a readable foreground (white or black) for a given background.
 * Used to derive --primary-foreground / --secondary-foreground when a
 * tenant overrides --primary / --secondary, so badge/button labels stay
 * legible regardless of the chosen brand color.
 */
export function deriveContrastingForeground(bgHex: string): string {
  // Pick whichever extreme actually contrasts more, rather than thresholding on
  // luminance < 0.5. The WCAG-optimal crossover is ~0.179, not 0.5, so the old
  // test handed WHITE to every mid-tone background: 69 of the 256 greys came
  // back below the 4.5:1 body-text floor (#aaaaaa → 2.32:1). Choosing the
  // higher-contrast option is never worse and brings that count to 0.
  //
  // Load-bearing in four places: the landing canvas ink (landing-renderer),
  // --primary-foreground for the tenant brand (get-branding-style), the
  // native accent (use-native-presentation) and customer email buttons.
  return contrastRatio(bgHex, '#000000') >= contrastRatio(bgHex, '#ffffff')
    ? '#000000'
    : '#ffffff';
}

/** Linear blend from `base` toward `tint` (0 = base, 1 = tint). */
export function mixHex(base: string, tint: string, amount: number): string {
  const a = hexToRgb(base);
  const b = hexToRgb(tint);

  return rgbToHex(
    a.r + (b.r - a.r) * amount,
    a.g + (b.g - a.g) * amount,
    a.b + (b.b - a.b) * amount,
  );
}
